import React, {useCallback, useEffect, useRef, useState} from "react";
import {
    ExpirationRecalculation,
    PersonDocumentType,
    PersonDocumentTypeRestControllerApiFactory
} from "arteco-api-client-ts";
import {FormContent} from "../../../../components/form/form_inputs/form_buttons";
import {FormInput} from "../../../../components/form/form_inputs/form_input";
import {useForm, UseFormProps} from "../../../../components/form/form_model";
import {useApiManager} from "../../../../components/utils/api";
import FormToggle from "../../../../components/form/form_inputs/form_toggle";
import FormSelect from "../../../../components/form/form_inputs/form_select";
import {types} from "../../../../components/form/types";
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    Stack,
    Typography
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import {useSnackbar} from "notistack";
import {btnSx} from "../../../../components/kit";
import {useConfirm} from "../../../../components/utils/dialogs";
import {monthsLabel} from "./labels";

type Mode = "NONE" | "MANUAL" | "AUTO";

/** Lo que eligió RR. HH. al guardar un cambio que afecta a documentos ya subidos. */
type SaveChoice = "cancel" | "save" | "recalculate";

const documentsLabel = (n: number) => n === 1 ? "1 documento" : n + " documentos";

/** Los que no tienen año y mes cuentan desde la subida hasta que se les pongan (#14387). */
const withoutYearMonthLabel = (n: number) => n === 1
    ? "1 no tiene año y mes y cuenta desde su fecha de subida."
    : n + " no tienen año y mes y cuentan desde su fecha de subida.";

/**
 * Tipo de documento del personal. La caducidad (#14381): no caduca, la indica quien lo sube (el DNI) o se
 * calcula sola a tantos meses del año y mes del documento (el perfil técnico, a 6). Un tipo automático pide
 * siempre año y mes (#14387); los documentos que no los tienen cuentan desde su subida.
 *
 * Guardar el tipo no toca nunca los documentos ya subidos. Recalcular su caducidad es «Recalcular ahora»,
 * que antes dice cuántos documentos cambian y lo confirma. Si al guardar se pasa a
 * automática o se cambian los meses, se pregunta también: guardar y recalcular, o solo guardar.
 *
 * Si es obligatorio, cada cuánto (#14415): una vez (basta uno vigente) o uno nuevo cada mes o cada año.
 */
export const PersonDocumentTypeEditPage = () => {
    const apiManager = useApiManager();
    const ctrl = apiManager.factory(PersonDocumentTypeRestControllerApiFactory);
    const {confirm, confirmDialog} = useConfirm();
    const {enqueueSnackbar} = useSnackbar();

    const [mode, setMode] = useState<Mode>("NONE");
    const [required, setRequired] = useState(false);
    const [period, setPeriod] = useState<string>("ONCE");
    const [months, setMonths] = useState<number | undefined>(undefined);
    const [stored, setStored] = useState<{ mode: Mode, months?: number } | undefined>(undefined);
    const [preview, setPreview] = useState<ExpirationRecalculation | undefined>(undefined);
    const [recalculating, setRecalculating] = useState(false);
    const [stale, setStale] = useState<string | undefined>(undefined);

    // la pregunta al guardar: cuántos cambiarían con los meses nuevos y qué se elige
    const [saveQuestion, setSaveQuestion] = useState<{ changes: number, months: number, resolve: (c: SaveChoice) => void } | undefined>(undefined);
    const recalculateAfterSave = useRef<number | undefined>(undefined);

    const loadPreview = useCallback((typeId?: number, storedMode?: Mode, storedMonths?: number) => {
        if (!typeId || storedMode !== "AUTO" || !storedMonths) {
            setPreview(undefined);
            return Promise.resolve(undefined);
        }
        return apiManager.execute(ctrl.previewPersonDocumentTypeRecalculationPersonDocumentType(typeId))
            .then(p => {
                setPreview(p);
                return p;
            });
    }, []);

    const recalculate = (typeId: number, expected: number) => {
        setRecalculating(true);
        setStale(undefined);
        return apiManager.executeRaw(ctrl.recalculatePersonDocumentTypeExpirationsPersonDocumentType(typeId, expected), undefined, true)
            .then(r => {
                enqueueSnackbar("Caducidad recalculada en " + documentsLabel(r.data.changes ?? 0), {variant: "success"});
                return loadPreview(typeId, "AUTO", r.data.months);
            })
            .catch(e => {
                if (e?.response?.status === 409) {
                    setStale("Mientras tanto han cambiado documentos de este tipo. Revisa el número nuevo y vuelve a recalcular.");
                    return loadPreview(typeId, "AUTO", months);
                }
                setStale("No se ha podido recalcular. Vuelve a intentarlo.");
            })
            .finally(() => setRecalculating(false));
    };

    const askOnSave = (changes: number, newMonths: number) =>
        new Promise<SaveChoice>(resolve => setSaveQuestion({changes, months: newMonths, resolve}));

    const answer = (choice: SaveChoice) => {
        saveQuestion?.resolve(choice);
        setSaveQuestion(undefined);
    };

    const form = useForm({
        fetcher: (id) => ctrl.getPersonDocumentType(id),
        updater: (bean) => ctrl.savePersonDocumentType(bean),
        remover: (id) => ctrl.deleteOrDismissPersonDocumentType(id),
        factory: () => {
            return {expirationMode: "NONE", requiredPeriod: "ONCE"} as PersonDocumentType;
        },
        postLoad: (bean) => {
            const m = (bean.expirationMode || "NONE") as Mode;
            setMode(m);
            setRequired(!!bean.requiredForAll);
            setPeriod(bean.requiredPeriod || "ONCE");
            setMonths(bean.expirationMonths);
            setStored({mode: m, months: bean.expirationMonths});
            loadPreview(bean.id, m, bean.expirationMonths);
        },
        // un cambio a automática o de meses en un tipo con documentos: se dice cuántos cambiarían y se elige
        preUpdate: async (bean) => {
            recalculateAfterSave.current = undefined;
            const newMode = (bean.expirationMode || "NONE") as Mode;
            const newMonths = Number(bean.expirationMonths) || 0;
            const changed = newMode === "AUTO" && newMonths > 0 && !!bean.id
                && (stored?.mode !== "AUTO" || stored?.months !== newMonths);
            if (!changed) return true;
            const p = await apiManager.execute(ctrl.previewPersonDocumentTypeRecalculationPersonDocumentType(bean.id!, newMonths));
            if (!p.changes) return true;
            const choice = await askOnSave(p.changes, newMonths);
            if (choice === "cancel") return false;
            if (choice === "recalculate") recalculateAfterSave.current = p.changes;
            return true;
        },
        postUpdate: (bean) => {
            const m = (bean.expirationMode || "NONE") as Mode;
            setStored({mode: m, months: bean.expirationMonths});
            const expected = recalculateAfterSave.current;
            recalculateAfterSave.current = undefined;
            if (bean.id && expected !== undefined) recalculate(bean.id, expected);
            else loadPreview(bean.id, m, bean.expirationMonths);
        },
    } as UseFormProps<PersonDocumentType>);

    const id = form.getId();
    useEffect(() => {
        if (!id) setPreview(undefined);
    }, [id]);

    const unsavedChange = mode === "AUTO" && !!months && months > 0 && !!id
        && (stored?.mode !== "AUTO" || stored?.months !== months);
    const storedAuto = stored?.mode === "AUTO" && !!stored?.months;

    const onRecalculateNow = async () => {
        if (!id || !preview) return;
        const ok = await confirm({
            title: "Recalcular la caducidad",
            message: <>
                Va a cambiar la caducidad de <b>{documentsLabel(preview.changes ?? 0)}</b> de
                los {preview.documents} de este tipo: pasa a ser el día 1 de su año y mes
                más {monthsLabel(preview.months)}.
                {!!preview.withoutYearMonth && <> {withoutYearMonthLabel(preview.withoutYearMonth)}</>} Las fechas
                que se pusieron a mano se pierden.
            </>,
            confirmLabel: "Recalcular " + documentsLabel(preview.changes ?? 0),
            danger: true,
        });
        if (ok) recalculate(id, preview.changes ?? 0);
    };

    return (
        <>
            {confirmDialog}
            <Dialog open={!!saveQuestion} onClose={() => answer("cancel")} maxWidth={"sm"} fullWidth>
                <DialogTitle>¿Recalcular también los ya subidos?</DialogTitle>
                <DialogContent>
                    <DialogContentText component={"div"}>
                        Con {monthsLabel(saveQuestion?.months)} desde el año y mes de cada documento, cambiaría la caducidad
                        de <b>{documentsLabel(saveQuestion?.changes ?? 0)}</b> ya subidos de este tipo. Puedes
                        guardar ahora y recalcularlos más tarde con «Recalcular ahora».
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{flexWrap: "wrap", gap: 1}}>
                    <Button onClick={() => answer("cancel")} sx={btnSx}>Cancelar</Button>
                    <Button onClick={() => answer("save")} variant="outlined" sx={btnSx}>Solo guardar</Button>
                    <Button onClick={() => answer("recalculate")} variant="contained" color="error" sx={btnSx} autoFocus>
                        Guardar y recalcular {documentsLabel(saveQuestion?.changes ?? 0)}
                    </Button>
                </DialogActions>
            </Dialog>

            <FormContent form={form} title={"Tipo de documento del personal"}>

                <Grid container spacing={3}>
                    <Grid item xs={12} sm>
                        <FormInput type={"text"} field={"name"} label={"Nombre"}/>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        {/* automática: se cuenta desde el año y mes, así que es obligatorio (#14387) */}
                        <FormToggle field={"requiredYearMonth"} label={"Requiere año y mes"} disabled={mode === "AUTO"}
                                    helperText={mode === "AUTO" ? "Obligatorio con la caducidad automática" : undefined}/>
                    </Grid>
                </Grid>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={mode === "AUTO" ? 8 : 12}>
                        <FormSelect field={"expirationMode"} label={"Caducidad"} type={types.expirationModes}
                                    onChange={item => {
                                        const m = ((item as any)?.value || "NONE") as Mode;
                                        setMode(m);
                                        if (m === "AUTO") form.triggerField("requiredYearMonth", true);
                                    }}/>
                    </Grid>
                    {mode === "AUTO" && (
                        <Grid item xs={12} sm={4}>
                            <FormInput type={"number"} field={"expirationMonths"} label={"Meses desde el año y mes"}
                                       inputProps={{min: 1, max: 600}}
                                       helperText={months ? "Caduca a " + monthsLabel(months) + " del mes del documento" : "6 = medio año, 120 = diez años"}
                                       onChange={v => setMonths(v === "" || v === undefined || v === null ? undefined : Number(v))}/>
                        </Grid>
                    )}
                </Grid>
                {mode === "MANUAL" && (
                    <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                        Quien lo sube tiene que indicar la fecha de caducidad, como en el DNI.
                    </Typography>
                )}
                {mode === "AUTO" && (
                    <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                        La fecha la pone la Extranet y nadie la puede cambiar: el día 1 del año y mes del documento
                        más {months ? monthsLabel(months) : "los meses"} (marzo de 2026 a 6 meses caduca el 01/09/2026).
                        Si un documento ya subido no tiene año y mes, cuenta desde su fecha de subida. Guardar el tipo
                        no cambia la de los documentos ya subidos.
                    </Typography>
                )}
                {unsavedChange && (
                    <Alert severity="info" sx={{mb: 2, borderRadius: 2}}>
                        Al guardar te dirá cuántos documentos ya subidos cambiarían con {monthsLabel(months)} y
                        podrás elegir si recalcularlos ahora o más tarde.
                    </Alert>
                )}
                {storedAuto && !unsavedChange && preview && (
                    <Alert severity={preview.changes ? "warning" : "success"} sx={{mb: 2, borderRadius: 2}}
                           action={preview.changes ? (
                               <Button color="inherit" size="small" sx={{...btnSx, whiteSpace: "nowrap"}}
                                       startIcon={<RefreshIcon fontSize="small"/>} disabled={recalculating}
                                       onClick={onRecalculateNow}>
                                   Recalcular ahora
                               </Button>
                           ) : undefined}>
                        {preview.changes
                            ? <>{documentsLabel(preview.changes)} de {preview.documents} tienen una caducidad distinta de
                                la que les toca: su año y mes más {monthsLabel(preview.months)}.</>
                            : <>Los {preview.documents} documentos de este tipo tienen ya la caducidad que les toca: su
                                año y mes más {monthsLabel(preview.months)}.</>}
                        {!!preview.withoutYearMonth && <> {withoutYearMonthLabel(preview.withoutYearMonth)}</>}
                    </Alert>
                )}
                {stale && (
                    <Alert severity="error" sx={{mb: 2, borderRadius: 2}} onClose={() => setStale(undefined)}>
                        {stale}
                    </Alert>
                )}
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={required ? 5 : 12}>
                        <FormToggle field={"requiredForAll"} label={"Obligatorio para toda la plantilla"}
                                    onChange={setRequired}/>
                    </Grid>
                    {/* una vez o uno nuevo cada mes o cada año aunque el anterior siga vigente (#14415) */}
                    {required && (
                        <Grid item xs={12} sm={7}>
                            <FormSelect field={"requiredPeriod"} label={"Cada cuánto"} type={types.requiredPeriods}
                                        onChange={item => setPeriod(((item as any)?.value || "ONCE") as string)}/>
                        </Grid>
                    )}
                </Grid>
                {required && (
                    <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                        {period === "MONTHLY"
                            ? "Cada persona tiene que tener el del último mes cerrado (en octubre, el de septiembre), aunque el anterior no haya caducado. Cuenta el año y mes del documento."
                            : period === "YEARLY"
                                ? "Cada persona tiene que tener el del último año cerrado (en 2026, el de 2025), aunque el anterior no haya caducado. Cuenta el año del documento. A quien entró después de ese año todavía no se le pide."
                                : "Basta con que cada persona tenga uno vigente: sin caducidad o con ella por llegar."}
                    </Typography>
                )}

                <FormInput type={"text"} field={"description"}
                           label={"Descripción visible por el empleado"} rows={10}/>

            </FormContent>
        </>
    );
};

